/**
 * Escala de cor da marca do estabelecimento.
 *
 * De uma cor escolhida na Personalizacao, deriva os onze tons que o tema usa
 * (50 a 950). Preserva matiz e saturacao e aplica a curva de luminosidade da
 * escala violeta de referencia, entao qualquer cor rende uma rampa utilizavel.
 *
 * Os valores saem como trio de canais RGB ("124 58 237") porque o tema declara
 * a paleta como `rgb(var(--brand-600) / <alpha-value>)` — e assim os usos com
 * transparencia (`bg-primary-500/20`) continuam funcionando.
 */

export const BRAND_STEPS = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950,
] as const;

export type BrandStep = (typeof BRAND_STEPS)[number];
export type BrandScale = Record<BrandStep, string>;

/** Luminosidade de cada tom, medida na escala violeta do tema. */
const LIGHTNESS_CURVE: Record<BrandStep, number> = {
  50: 97.6,
  100: 95.5,
  200: 91.8,
  300: 85.5,
  400: 76.3,
  500: 66.3,
  600: 57.5,
  700: 50.4,
  800: 41.8,
  900: 34.5,
  950: 22.5,
};

/**
 * Violeta do Agiliza: a cor da marca de quem nao escolheu a sua. Vale para a
 * paleta inteira e para o fundo do cabecalho da pagina publica.
 */
export const DEFAULT_BRAND_HEX = "#7c3aed";
/** Violeta do estado de hover, um passo mais escuro que o 600. */
export const DEFAULT_BRAND_HOVER = "109 40 217";
/** Tons violeta usados no degrade do cabecalho sem cor escolhida. */
export const DEFAULT_BRAND_DEEP_HEX = "#6d28d9";
export const DEFAULT_BRAND_DEEPEST_HEX = "#4c1d95";

/** Violeta atual do Agiliza, tom por tom, para quem nao escolheu cor. */
export const DEFAULT_BRAND_SCALE: BrandScale = {
  50: "245 243 255",
  100: "237 233 254",
  200: "221 214 254",
  300: "196 181 253",
  400: "167 139 250",
  500: "139 92 246",
  600: "124 58 237",
  700: "109 40 217",
  800: "91 33 182",
  900: "76 29 149",
  950: "46 16 101",
};

/** Contraste minimo do texto branco sobre o tom dos botoes (WCAG AA). */
const MIN_CONTRAST_ON_WHITE = 4.5;

/**
 * Luminosidade minima do tom 600. Preto puro ancoraria o botao em zero e os
 * tons de baixo (hover, foco) nao teriam para onde escurecer — o botao pararia
 * de responder ao toque aos olhos de quem usa. Nesse ponto ainda le como preto.
 */
const MIN_ANCHOR_LIGHTNESS = 10;

/** Piso de luminosidade: abaixo disso os tons escuros viram todos preto. */
const MIN_DARK_LIGHTNESS = 6;

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function parseHexColor(value?: string | null): Rgb | null {
  let hex = String(value || "").trim();
  if (!hex.startsWith("#")) return null;
  if (hex.length === 4) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;

  const numeric = parseInt(hex.slice(1), 16);
  return {
    r: (numeric >> 16) & 255,
    g: (numeric >> 8) & 255,
    b: numeric & 255,
  };
}

function rgbToHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const l = (max + min) / 2;

  if (delta === 0) return { h: 0, s: 0, l: l * 100 };

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === red) h = ((green - blue) / delta) % 6;
  else if (max === green) h = (blue - red) / delta + 2;
  else h = (red - green) / delta + 4;

  h *= 60;
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const saturation = Math.min(100, Math.max(0, s)) / 100;
  const lightness = Math.min(100, Math.max(0, l)) / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const hPrime = (((h % 360) + 360) % 360) / 60;
  const x = chroma * (1 - Math.abs((hPrime % 2) - 1));

  const [r1, g1, b1] =
    hPrime < 1
      ? [chroma, x, 0]
      : hPrime < 2
        ? [x, chroma, 0]
        : hPrime < 3
          ? [0, chroma, x]
          : hPrime < 4
            ? [0, x, chroma]
            : hPrime < 5
              ? [x, 0, chroma]
              : [chroma, 0, x];

  const m = lightness - chroma / 2;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Contraste entre uma cor e o branco, na escala WCAG de 1 a 21. */
export function contrastWithWhite(rgb: Rgb): number {
  const luminance = relativeLuminance(rgb);
  return 1.05 / (luminance + 0.05);
}

/**
 * Maior luminosidade que ainda deixa texto branco legivel neste matiz. Amarelo
 * precisa escurecer muito mais que azul para chegar ao mesmo contraste, por
 * isso a busca e por matiz e nao um valor fixo.
 */
function maxLightnessForWhiteText(h: number, s: number): number {
  let candidate = 0;
  for (let l = 100; l >= 0; l -= 0.5) {
    if (contrastWithWhite(hslToRgb(h, s, l)) >= MIN_CONTRAST_ON_WHITE) {
      candidate = l;
      break;
    }
  }
  return candidate;
}

function toChannels({ r, g, b }: Rgb): string {
  return `${r} ${g} ${b}`;
}

/**
 * Deriva a escala da marca. Cor invalida ou ausente devolve o violeta padrao,
 * para nunca deixar a pagina sem paleta.
 *
 * O tom 600 — fundo de botao, cor de icone, o rosto do estabelecimento — e a
 * propria cor escolhida, e a rampa se reancora nele. Antes a escala usava só o
 * matiz e a saturacao da escolha e impunha a luminosidade da curva do violeta:
 * preto virava cinza medio, e preto e branco rendiam a mesma escala.
 */
export function buildBrandScale(color?: string | null): BrandScale {
  const rgb = parseHexColor(color);
  if (!rgb) return DEFAULT_BRAND_SCALE;

  const { h, s, l } = rgbToHsl(rgb);

  /**
   * A ancora e a cor escolhida, cedendo em dois casos: cor clara demais
   * escurece ate o texto branco ficar legivel (amarelo), e cor quase preta
   * sobe ao piso para sobrar rampa abaixo dela.
   */
  const ceiling = maxLightnessForWhiteText(h, s);
  const anchor = Math.max(MIN_ANCHOR_LIGHTNESS, Math.min(l, ceiling));

  /** O extremo escuro guarda a mesma proporcao que tinha na curva original. */
  const darkFloor = Math.min(
    Math.max(MIN_DARK_LIGHTNESS, anchor * (LIGHTNESS_CURVE[950] / LIGHTNESS_CURVE[600])),
    anchor - 1
  );

  const top = LIGHTNESS_CURVE[50];
  const scale = {} as BrandScale;

  for (const step of BRAND_STEPS) {
    /**
     * Remapeia por partes, mantendo o ritmo da curva original: 50 continua
     * claro o bastante para servir de fundo, 600 cai exatamente na ancora e
     * 950 no piso. Uma unica reta faria o tom 50 acompanhar a ancora e uma
     * marca escura perderia os fundos claros.
     */
    const lightness =
      step <= 600
        ? anchor +
          ((LIGHTNESS_CURVE[step] - LIGHTNESS_CURVE[600]) /
            (top - LIGHTNESS_CURVE[600])) *
            (top - anchor)
        : anchor -
          ((LIGHTNESS_CURVE[600] - LIGHTNESS_CURVE[step]) /
            (LIGHTNESS_CURVE[600] - LIGHTNESS_CURVE[950])) *
            (anchor - darkFloor);

    scale[step] = toChannels(hslToRgb(h, s, lightness));
  }

  return scale;
}

/** Quanto o hover se afasta da cor base, em pontos de luminosidade. */
const HOVER_SHIFT = 9;

/**
 * Tom do botao sob o cursor ou o dedo. Nenhuma direcao fixa serve para toda
 * cor: uma marca preta nao tem para onde escurecer (o hover sairia 2 pontos de
 * canal abaixo da base, invisivel) e uma marca amarela nao tem para onde
 * clarear sem o texto branco ficar ilegivel. Entao o tom anda para o lado que
 * tiver espaco, e o resultado vira a variavel `--brand-hover`.
 */
export function buildBrandHover(color?: string | null): string {
  const rgb = parseHexColor(color);
  if (!rgb) return DEFAULT_BRAND_HOVER;

  const { h, s, l } = rgbToHsl(rgb);
  const ceiling = maxLightnessForWhiteText(h, s);
  const anchor = Math.max(MIN_ANCHOR_LIGHTNESS, Math.min(l, ceiling));

  const espacoParaClarear = ceiling - anchor;
  const espacoParaEscurecer = anchor - MIN_DARK_LIGHTNESS;

  const lightness =
    espacoParaClarear >= espacoParaEscurecer
      ? Math.min(anchor + HOVER_SHIFT, ceiling)
      : Math.max(anchor - HOVER_SHIFT, MIN_DARK_LIGHTNESS);

  return toChannels(hslToRgb(h, s, lightness));
}

/** Variaveis CSS da escala, para aplicar num escopo da pagina. */
export function buildBrandCssVariables(color?: string | null): Record<string, string> {
  const scale = buildBrandScale(color);
  const vars = BRAND_STEPS.reduce<Record<string, string>>((acc, step) => {
    acc[`--brand-${step}`] = scale[step];
    return acc;
  }, {});
  vars["--brand-hover"] = buildBrandHover(color);
  return vars;
}

/**
 * Cor de texto legivel sobre um fundo. Devolve o quase-branco ou o quase-preto
 * do tema, o que tiver mais contraste — usado quando o dono nao escolheu cor de
 * texto e o fundo e uma cor forte.
 *
 * Sem isso, o titulo do cabecalho saia sempre quase-branco: sobre um fundo
 * amarelo isso rende contraste de 1,46, ilegivel.
 */
export function resolveReadableTextColor(backgroundColor?: string | null): string | null {
  const rgb = parseHexColor(backgroundColor);
  if (!rgb) return null;

  const claro = { r: 248, g: 250, b: 252 };
  const escuro = { r: 15, g: 23, b: 42 };

  const contrasteClaro = contrastBetween(rgb, claro);
  const contrasteEscuro = contrastBetween(rgb, escuro);
  return contrasteEscuro > contrasteClaro ? "#0f172a" : "#f8fafc";
}

function contrastBetween(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Bloco `:root` com o violeta padrao da marca em canais RGB.
 *
 * O tema declara `primary` como `rgb(var(--brand-NNN) / <alpha-value>)`, entao
 * sem estas variaveis **nenhuma** classe `primary` produz cor: a declaracao e
 * invalida e o navegador a descarta. Um botao `bg-primary-600 text-white` fica
 * branco sobre branco, ou seja, invisivel.
 *
 * As duas arvores de rota precisam injetar isto — o App Router pelo
 * `app/layout.tsx` e o Pages Router pelo `pages/_document.tsx`, que nao passa
 * pelo layout do App Router. Mantenha uma fonte so para as duas nao divergirem.
 */
export function buildDefaultBrandRootCss(): string {
  const linhas = BRAND_STEPS.map(
    (step) => `--brand-${step}: ${DEFAULT_BRAND_SCALE[step]};`
  );
  linhas.push(`--brand-hover: ${DEFAULT_BRAND_HOVER};`);
  return `:root {\n  ${linhas.join("\n  ")}\n}`;
}
